import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import {
	Menu,
	Item,
	Separator,
	Submenu,
	useContextMenu,
} from "react-contexify";
import "react-contexify/dist/ReactContexify.css";
import stc from "string-to-color";

import { cuesApi, timersApi } from "../imports";
import { definitions } from "../imports";
import { msToString, stringToMS } from "./helpers/time";
import { containsDataSourceList, extractNumberFromList } from "./helpers/variable";
import { useIsPro } from "../imports";
import { H2RButton } from "../imports";
import CueStatus from "./CueStatus";
import { faArrowDown, faArrowLeft, faArrowRight, faArrowUp, faChevronDown, faPause, faPlay, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip, TooltipContent, TooltipTrigger } from "./snippets/Tooltip";

export const cueDesign = (type) => {
	let iconColour;
	let ringColour = "";
	let icon;

	switch (type) {
		case "lower_third":
			iconColour = "bg-yellow-600";
			ringColour = "ring-yellow-600";
			icon = (
				<>
					<rect
						x="7"
						y="30"
						width="34.895"
						height="4.758"
						rx="2.379"
						fill="#fff"
					/>
					<rect
						x="7"
						y="37.402"
						width="22.735"
						height="3.701"
						rx="1.85"
						fill="#fff"
					/>
				</>
			);
			break;

		case "lower_third_animated":
			iconColour = "bg-gradient-to-r from-yellow-400 to-yellow-600";
			ringColour = "ring-yellow-600";
			icon = (
				<>
					<rect
						x="7"
						y="30"
						width="34.895"
						height="4.758"
						rx="2.379"
						fill="#fff"
					/>
					<rect
						x="7"
						y="37.402"
						width="22.735"
						height="3.701"
						rx="1.85"
						fill="#fff"
					/>
				</>
			);

			break;

		case "message":
			iconColour = "bg-yellow-500";
			ringColour = "ring-yellow-500";
			icon = (
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M40.7 28.8V16c0-.849-.348-1.663-.967-2.263A3.353 3.353 0 0037.4 12.8H17.6c-.875 0-1.715.337-2.333.937A3.151 3.151 0 0014.3 16v12.8c0 .848.348 1.662.967 2.262.619.6 1.458.938 2.333.938h4.95l4.95 4.8 4.95-4.8h4.95c.875 0 1.715-.337 2.334-.938.618-.6.966-1.414.966-2.262zm-21.45-9.6c0-.424.174-.831.483-1.132.31-.3.73-.468 1.167-.468h13.2c.438 0 .857.168 1.167.468.31.3.483.707.483 1.132 0 .424-.174.831-.483 1.131-.31.3-.73.469-1.167.469H20.9c-.438 0-.857-.169-1.167-.469-.31-.3-.483-.707-.483-1.131zM20.9 24c-.438 0-.857.168-1.167.468-.31.3-.483.707-.483 1.132 0 .424.174.831.483 1.131.31.3.73.469 1.167.469h4.95c.438 0 .857-.169 1.167-.469.31-.3.483-.707.483-1.131 0-.425-.174-.832-.483-1.132-.31-.3-.73-.468-1.167-.468H20.9z"
					fill="#fff"
				/>
			);
			break;

		case "credits":
			iconColour = "bg-gradient-to-r from-green-500 to-green-800";
			ringColour = "ring-green-800";
			icon = (
				<>
					<rect
						x="19.105"
						y="22.544"
						width="16.895"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
					<rect
						x="15.105"
						y="14"
						width="24.895"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
					<rect
						x="17.105"
						y="30.312"
						width="20.895"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
				</>
			);
			break;

		case "icon_with_message":
			iconColour = "bg-gradient-to-r from-yellow-500 to-yellow-700";
			ringColour = "ring-yellow-700";
			icon = (
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					d="M40.7 28.8V16c0-.849-.348-1.663-.967-2.263A3.353 3.353 0 0037.4 12.8H17.6c-.875 0-1.715.337-2.333.937A3.151 3.151 0 0014.3 16v12.8c0 .848.348 1.662.967 2.262.619.6 1.458.938 2.333.938h4.95l4.95 4.8 4.95-4.8h4.95c.875 0 1.715-.337 2.334-.938.618-.6.966-1.414.966-2.262zm-21.45-9.6c0-.424.174-.831.483-1.132.31-.3.73-.468 1.167-.468h13.2c.438 0 .857.168 1.167.468.31.3.483.707.483 1.132 0 .424-.174.831-.483 1.131-.31.3-.73.469-1.167.469H20.9c-.438 0-.857-.169-1.167-.469-.31-.3-.483-.707-.483-1.131zM20.9 24c-.438 0-.857.168-1.167.468-.31.3-.483.707-.483 1.132 0 .424.174.831.483 1.131.31.3.73.469 1.167.469h4.95c.438 0 .857-.169 1.167-.469.31-.3.483-.707.483-1.131 0-.425-.174-.832-.483-1.132-.31-.3-.73-.468-1.167-.468H20.9z"
					fill="#fff"
				/>
			);
			break;
		case "solid_colour":
			iconColour = "bg-gray-700";
			ringColour = "ring-yellow-700";
			icon = (
				<path
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="3"
					d="M16.805 35.194a5.312 5.312 0 0 0 3.758 1.557m-3.758-1.556a5.314 5.314 0 0 0 3.758 1.556m-3.758-1.556a5.313 5.313 0 0 1-1.555-3.758V12.845c0-.88.714-1.594 1.594-1.594h7.437c.88 0 1.594.714 1.594 1.594v5.768m-5.313 18.139a5.313 5.313 0 0 0 3.758-1.556m-3.758 1.556c1.41 0 2.761-.56 3.758-1.556m-3.758 1.556 18.594-.001c.88 0 1.594-.714 1.594-1.594V27.72c0-.88-.714-1.594-1.594-1.594h-5.768m-9.068 9.07 9.068-9.07m-9.068 9.07a5.313 5.313 0 0 0 1.555-3.758V18.613m7.513 7.513 4.078-4.08a1.59 1.59 0 0 0 0-2.253l-5.258-5.26a1.593 1.593 0 0 0-2.253 0l-4.08 4.08m-5.313 12.826h.012v.01h-.012v-.01Z"
				/>
			);
			break;

		case "time":
		case "time_countdown":
		case "time_countup":
		case "time_tod":
		case "time_to_tod":
			iconColour = "bg-red-400";
			ringColour = "ring-red-400";
			icon = (
				<path
					d="M27 19.333V24l3.5 3.5m7-3.5a10.5 10.5 0 11-21 0 10.5 10.5 0 0121 0z"
					stroke="#fff"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);

			break;

		case "big_time":
		case "big_time_countdown":
		case "big_time_countup":
		case "big_time_to_tod":
			iconColour = "bg-gradient-to-r from-red-400 to-red-600";
			ringColour = "ring-red-600";
			icon = (
				<path
					d="M27 19.333V24l3.5 3.5m7-3.5a10.5 10.5 0 11-21 0 10.5 10.5 0 0121 0z"
					stroke="#fff"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);

			break;

		case "image":
			iconColour = "bg-blue-600";
			ringColour = "ring-blue-600";
			icon = (
				<path
					d="M18.5 28l5.16-5.16a2.25 2.25 0 013.18 0L32 28m-2.25-2.25l1.784-1.784a2.25 2.25 0 013.182 0L36.5 25.75M29.75 19h.011M20.75 32.5h13.5a2.25 2.25 0 002.25-2.25v-13.5a2.25 2.25 0 00-2.25-2.25h-13.5a2.25 2.25 0 00-2.25 2.25v13.5a2.25 2.25 0 002.25 2.25z"
					stroke="#fff"
					strokeWidth="3"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;
		
		case "image_sequence":
			iconColour = "bg-gradient-to-r from-blue-500 to-purple-800";
			ringColour = "ring-blue-600";
			icon = (
				<>
					<path 
						d="M16.687 31.1h-1.596c-1.085 0-2.126-.36-2.893-1-.767-.64-1.198-1.507-1.198-2.413v-5.375c0-.905.431-1.773 1.198-2.413.767-.64 1.808-.999 2.893-.999h1.596m0 12.2a2.903 2.903 0 0 1-.233-1.138v-9.924c0-.4.082-.782.233-1.138m0 12.2c.282.665.805 1.242 1.496 1.65a4.671 4.671 0 0 0 2.363.625h1.596c-.151-.356-.142-.83-.142-1.228V17.5c0-.387-.245-.51-.09-.875h-1.364c-.847 0-1.672.218-2.363.626-.691.407-1.214.983-1.496 1.649" 
						stroke="currentColor" 
						strokeWidth="2" 
						strokeLinecap="round" 
						strokeLinejoin="round"
						/>
					<path 
						d="m22 29.75 5.446-6.02a2.266 2.266 0 0 1 1.679-.768c.63 0 1.234.277 1.68.769l5.445 6.019m-2.375-2.625 1.883-2.082a2.266 2.266 0 0 1 1.68-.768c.63 0 1.233.276 1.679.768L41 27.125m-7.125-7.875h.012M24.375 35h14.25c.63 0 1.234-.277 1.68-.769.445-.492.695-1.16.695-1.856v-15.75c0-.696-.25-1.364-.696-1.856A2.267 2.267 0 0 0 38.625 14h-14.25c-.63 0-1.234.277-1.68.769A2.772 2.772 0 0 0 22 16.625v15.75c0 .696.25 1.364.696 1.856.445.492 1.05.769 1.679.769Z" 
						stroke="currentColor" 
						strokeWidth="2" 
						strokeLinecap="round" 
						strokeLinejoin="round"
					/>
				</>
			);
			break;
		
		case "slideshow":
			iconColour = "bg-gradient-to-r from-blue-700 to-purple-800";
			ringColour = "ring-blue-600";
			icon = (
				<>
					<path
						d="m18.5 28 5.16-5.16a2.25 2.25 0 0 1 3.18 0L32 28m-2.25-2.25 1.784-1.784a2.25 2.25 0 0 1 3.182 0L36.5 25.75M29.75 19h.011M20.75 32.5h13.5a2.25 2.25 0 0 0 2.25-2.25v-13.5a2.25 2.25 0 0 0-2.25-2.25h-13.5a2.25 2.25 0 0 0-2.25 2.25v13.5a2.25 2.25 0 0 0 2.25 2.25Z"
						stroke="currentColor"
						strokeWidth="3"
						strokeLinecap="round"
						strokeLinejoin="round"/>
					<path
						d="M12.875 26.75 9.125 23l3.75-3.75M42.125 19.25l3.75 3.75-3.75 3.75"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			);
			break;

		case "image_with_message":
			iconColour = "bg-gradient-to-r from-blue-400 to-blue-700";
			ringColour = "ring-blue-600";
			icon = (
				<path
					d="M18.5 28l5.16-5.16a2.25 2.25 0 013.18 0L32 28m-2.25-2.25l1.784-1.784a2.25 2.25 0 013.182 0L36.5 25.75M29.75 19h.011M20.75 32.5h13.5a2.25 2.25 0 002.25-2.25v-13.5a2.25 2.25 0 00-2.25-2.25h-13.5a2.25 2.25 0 00-2.25 2.25v13.5a2.25 2.25 0 002.25 2.25z"
					stroke="#fff"
					strokeWidth="3"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "video":
			iconColour = "bg-gradient-to-r from-indigo-400 to-indigo-600";
			ringColour = "ring-indigo-600";

			icon = (
				<>
					<path
						d="m31.44 22.96-3.996-2.665a1.25 1.25 0 0 0-1.944 1.043v5.328a1.25 1.25 0 0 0 1.944 1.04l3.996-2.665a1.25 1.25 0 0 0 0-2.08v0Z"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M39.25 24a11.25 11.25 0 1 1-22.499 0 11.25 11.25 0 0 1 22.499 0Z"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			);
			break;

		case "lyrics":
			iconColour = "bg-gradient-to-r from-purple-300 to-purple-500";
			ringColour = "ring-indigo-600";

			icon = (
				<>
					<rect
						width="27.265"
						height="3.701"
						x="14.735"
						y="40"
						fill="#fff"
						rx="1.85"
					/>
					<rect
						width="39.265"
						height="3.701"
						x="8.735"
						y="33"
						fill="#fff"
						rx="1.85"
					/>
				</>
			);
			break;

		case "audio":
			iconColour = "bg-gradient-to-r from-indigo-300 to-indigo-500";
			ringColour = "ring-indigo-600";

			icon = (
				<path
					stroke="currentColor"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth="2"
					d="m24 20 14-4m0 8.737v5a3 3 0 0 1-2.176 2.884l-1.76.503a2.404 2.404 0 1 1-1.32-4.623l3.08-.88A3 3 0 0 0 38 24.737Zm0 0V11l-14 4v13.737m0 0v5a3 3 0 0 1-2.176 2.884l-1.76.503a2.404 2.404 0 0 1-1.32-4.623l3.08-.88A3 3 0 0 0 24 28.737Z"
				/>
			);
			break;

		case "ticker":
			iconColour = "bg-indigo-300";
			ringColour = "ring-indigo-300";
			icon = <rect x="7" y="37" width="42" height="4" rx="2" fill="#fff" />;
			break;

		case "checklist":
			iconColour = "bg-indigo-500";
			ringColour = "ring-indigo-500";
			icon = (
				<path
					d="M17 14h22m-22 6.333h12.158M17 26.667h22M17 33h16.645"
					stroke="currentColor"
					strokeWidth="4"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "social":
			iconColour = "bg-purple-700";
			ringColour = "ring-purple-700";
			icon = (
				<path
					d="M22.333 24h.015M28 24h.014m5.653 0h.014m7.069 0c0 6.259-5.71 11.333-12.75 11.333a13.973 13.973 0 01-6.028-1.344l-6.722 1.344 1.976-5.27C15.976 28.31 15.25 26.23 15.25 24c0-6.259 5.71-11.334 12.75-11.334S40.75 17.741 40.75 24z"
					stroke="currentColor"
					strokeWidth="3"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "celebration":
			iconColour = "bg-gradient-to-r from-purple-500 to-purple-700";
			ringColour = "ring-purple-700";
			icon = (
				<>
					<path
						d="M35 19.8c0 2.204-.82 4.601-2.152 6.435C31.508 28.08 29.772 29.2 28 29.2c-1.772 0-3.507-1.12-4.848-2.965C21.82 24.4 21 22.005 21 19.8c0-4.4 3.222-7.8 7-7.8s7 3.4 7 7.8Z"
						stroke="currentColor"
						strokeWidth="3"
					/>
					<path
						d="M28 29.5c-.533 1.067-1.078 1.9-.678 4.3.32 1.918 1.067 2.8 1.6 3.2"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
					/>
				</>
			);
			break;

		case "now_next_then":
			iconColour = "bg-gradient-to-r from-emerald-500 to-emerald-900";
			ringColour = "ring-purple-700";
			icon = (
				<>
					<g clipPath="url(#now_next_then_logo)">
						<path
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M35.45 15.333h1.277c.868 0 1.7.343 2.314.952.614.61.959 1.436.959 2.298v10.834c0 .862-.345 1.688-.959 2.298a3.284 3.284 0 0 1-2.314.952H35.45m0-17.334c.12.34.186.704.186 1.084v15.166c0 .38-.065.744-.186 1.084m0-17.334a3.252 3.252 0 0 0-1.196-1.57 3.288 3.288 0 0 0-1.89-.596h-1.277m4.363 19.5a3.252 3.252 0 0 1-1.196 1.57 3.288 3.288 0 0 1-1.89.596h-1.277m0 0a3.22 3.22 0 0 0 .186-1.083v-19.5c0-.38-.066-.744-.186-1.083m0 21.666a3.252 3.252 0 0 1-1.197 1.57A3.289 3.289 0 0 1 28 37h-8.727c-.868 0-1.7-.342-2.314-.952A3.238 3.238 0 0 1 16 33.75v-19.5c0-.862.345-1.689.959-2.298A3.284 3.284 0 0 1 19.273 11H28c1.425 0 2.637.904 3.087 2.167"
						/>
					</g>
					<defs>
						<clipPath id="now_next_then_logo">
							<path fill="currentColor" d="M42 38H14V10h28z" />
						</clipPath>
					</defs>
				</>
			);
			break;

		case "webpage":
			iconColour = "bg-indigo-800";
			ringColour = "ring-indigo-800";
			icon = (
				<path
					d="M16.156 22.676h2.575a2.648 2.648 0 012.648 2.648v1.324a2.648 2.648 0 002.649 2.649 2.648 2.648 0 012.648 2.648v3.899m-3.972-22.523v2.073a3.31 3.31 0 003.31 3.31h.662a2.648 2.648 0 012.648 2.648 2.648 2.648 0 005.296 0 2.648 2.648 0 012.648-2.648h1.41m-6.706 16.535v-3.294a2.648 2.648 0 012.648-2.648h4.057M39.917 24a11.916 11.916 0 11-23.833 0 11.916 11.916 0 0123.833 0z"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "score":
			iconColour = "bg-green-500";
			ringColour = "ring-green-500";
			icon = (
				<>
					<path
						d="M39.333 16.917 28 11.25l-11.333 5.667m22.666 0L28 22.583m11.333-5.666v14.166L28 36.75M16.667 16.917 28 22.583m-11.333-5.666v14.166L28 36.75m0-14.167V36.75"
						stroke="currentColor"
						strokeWidth="3"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<ellipse cx="31.5" cy="25" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="31.5" cy="31" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="36.5" cy="22" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="36.5" cy="28" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="28" cy="16.5" rx="1" ry=".5" fill="currentColor" />
					<ellipse cx="19.5" cy="23" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="24.5" cy="25" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="21.5" cy="27" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="19.5" cy="29" rx=".5" ry="1" fill="currentColor" />
					<ellipse cx="24.5" cy="31" rx=".5" ry="1" fill="currentColor" />
				</>
			);
			break;

		case "section":
			iconColour = "bg-gray-900";
			// icon = (
			// 	<path
			// 		d="M9 24h38"
			// 		stroke="currentColor"
			// 		strokeWidth="1"
			// 		strokeLinecap="round"
			// 		strokeLinejoin="round"
			// 	/>
			// );
			break;

		case "qr":
			iconColour = "bg-gradient-to-r from-green-700 to-green-900";
			ringColour = "ring-green-900";
			icon = (
				<>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M16.313 13.906c0-.88.713-1.594 1.593-1.594h6.375c.88 0 1.594.715 1.594 1.594v6.375c0 .88-.714 1.594-1.594 1.594h-6.375a1.594 1.594 0 0 1-1.593-1.594v-6.375Zm0 13.813c0-.88.713-1.594 1.593-1.594h6.375c.88 0 1.594.714 1.594 1.594v6.375c0 .88-.714 1.593-1.594 1.593h-6.375a1.594 1.594 0 0 1-1.593-1.593v-6.375Zm13.812-13.813c0-.88.714-1.594 1.594-1.594h6.375c.88 0 1.593.715 1.593 1.594v6.375c0 .88-.713 1.594-1.593 1.594h-6.375a1.594 1.594 0 0 1-1.594-1.594v-6.375Z"
					/>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M20.563 16.563h1.062v1.062h-1.063v-1.063Zm0 13.812h1.062v1.063h-1.063v-1.063Zm13.812-13.813h1.063v1.063h-1.063v-1.063Zm-4.25 9.563h1.063v1.063h-1.063v-1.063Zm0 8.5h1.063v1.063h-1.063v-1.063Zm8.5-8.5h1.063v1.063h-1.063v-1.063Zm0 8.5h1.063v1.063h-1.063v-1.063Zm-4.25-4.25h1.063v1.063h-1.063v-1.063Z"
					/>
				</>
			);
			break;

		case "pdf":
			iconColour = "bg-gradient-to-r from-red-700 to-red-900";
			ringColour = "ring-red-900";
			icon = (
				<>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="3"
						d="M38 27v-3.5a4.5 4.5 0 0 0-4.5-4.5h-2a1.5 1.5 0 0 1-1.5-1.5v-2a4.5 4.5 0 0 0-4.5-4.5H23m3 0h-6.5a1.5 1.5 0 0 0-1.5 1.5v23a1.5 1.5 0 0 0 1.5 1.5h17a1.5 1.5 0 0 0 1.5-1.5V23a12 12 0 0 0-12-12v0Z"
					/>
					<path
						fill="currentColor"
						d="M22.427 32v-4.364h1.803c.326 0 .608.064.845.192.239.127.423.303.552.53.13.226.194.49.194.789 0 .301-.066.565-.198.79a1.33 1.33 0 0 1-.56.522 1.886 1.886 0 0 1-.865.186h-1.113v-.831h.916c.16 0 .292-.028.399-.083a.57.57 0 0 0 .245-.232.719.719 0 0 0 .083-.352.699.699 0 0 0-.083-.35.557.557 0 0 0-.245-.23.884.884 0 0 0-.398-.08h-.52V32h-1.055Zm5.527 0H26.34v-4.364h1.61c.445 0 .828.088 1.15.262.322.174.57.424.745.75.175.326.262.715.262 1.168 0 .455-.087.845-.262 1.172a1.792 1.792 0 0 1-.744.752c-.322.173-.705.26-1.148.26Zm-.558-.9h.517c.245 0 .451-.04.62-.123a.817.817 0 0 0 .386-.407c.088-.189.132-.44.132-.754s-.044-.564-.132-.75a.814.814 0 0 0-.39-.405 1.422 1.422 0 0 0-.63-.126h-.503v2.566Zm3.328.9v-4.364h2.979v.857h-1.925v.895h1.735v.858h-1.734V32h-1.055Z"
					/>
				</>
			);
			break;

		case "map":
			iconColour = "bg-blue-900";
			ringColour = "ring-red-900";
			icon = (
				<path
					d="M23.25 15.6874V28.7499M32.75 19.2499V32.3124M33.5464 37.8509L41.2652 33.9923C41.8684 33.6915 42.25 33.074 42.25 32.3995V12.6316C42.25 11.3079 40.8567 10.4466 39.6723 11.0387L33.5464 14.1009C33.0445 14.3526 32.4539 14.3526 31.9536 14.1009L24.0464 10.1489C23.7991 10.0253 23.5265 9.96094 23.25 9.96094C22.9735 9.96094 22.7009 10.0253 22.4536 10.1489L14.7348 14.0075C14.13 14.3099 13.75 14.9274 13.75 15.6003V35.3682C13.75 36.6919 15.1433 37.5532 16.3277 36.9611L22.4536 33.8989C22.9555 33.6471 23.5461 33.6471 24.0464 33.8989L31.9536 37.8525C32.4555 38.1027 33.0461 38.1027 33.5464 37.8525V37.8509Z"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "animated_background":
			iconColour = "bg-gradient-to-r from-blue-400 to-blue-600";
			ringColour = "ring-red-600";
			icon = (
				<path
					d="M36.167 33.333H19.833A2.334 2.334 0 0 1 17.5 31V17a2.333 2.333 0 0 1 2.333-2.334H31.5A2.333 2.333 0 0 1 33.833 17v1.166m2.334 15.167A2.333 2.333 0 0 1 33.833 31V18.166m2.334 15.167A2.334 2.334 0 0 0 38.5 31V20.5a2.333 2.333 0 0 0-2.333-2.334h-2.334m-4.666-3.5H24.5m-2.333 4.667h7v9.334h-7v-9.334Z"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);

			break;

		case "utility_pattern":
			iconColour = "bg-gradient-to-b from-gray-700 to-gray-900";
			ringColour = "ring-red-600";
			icon = (
				<>
					<rect
						width="8"
						height="26"
						x="24"
						y="16"
						fill="currentColor"
						fillOpacity=".6"
						rx="1"
					/>
					<rect
						width="8"
						height="26"
						x="16"
						y="16"
						fill="currentColor"
						fillOpacity=".4"
						rx="1"
					/>
					<rect
						width="8"
						height="26"
						x="8"
						y="16"
						fill="currentColor"
						fillOpacity=".2"
						rx="1"
					/>
					<rect
						width="8"
						height="26"
						x="32"
						y="16"
						fill="currentColor"
						fillOpacity=".8"
						rx="1"
					/>
					<rect
						width="8"
						height="26"
						x="40"
						y="16"
						fill="currentColor"
						rx="1"
					/>
					<path
						fill="currentColor"
						d="M17.874 5.91h1.077v3.305c0 .371-.09.696-.267.975a1.78 1.78 0 0 1-.738.65 2.5 2.5 0 0 1-1.106.232c-.423 0-.792-.077-1.109-.231a1.779 1.779 0 0 1-.738-.651 1.787 1.787 0 0 1-.263-.975V5.91h1.076v3.214c0 .194.042.366.127.517.086.151.207.27.363.356.155.086.337.129.544.129.209 0 .39-.043.544-.13a.908.908 0 0 0 .36-.355c.087-.15.13-.323.13-.517V5.91Zm1.768.887v-.888h4.181v.888h-1.558V11H21.2V6.797h-1.559Zm5.946-.888V11h-1.076V5.91h1.076ZM26.474 11V5.91h1.076v4.203h2.183V11h-3.26Zm5.048-5.09V11h-1.077V5.91h1.077Zm.691.887v-.888h4.181v.888h-1.558V11h-1.064V6.797h-1.559Zm4.572-.888h1.205l1.161 2.193h.05l1.16-2.193h1.207L39.71 9.2V11h-1.07V9.2l-1.856-3.29Z"
					/>
				</>
			);

			break;

		case "utility_time_of_day":
			iconColour = "bg-gradient-to-b from-gray-700 to-gray-900";
			ringColour = "ring-red-600";
			icon = (
				<>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M28 23v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
					/>
					<path
						fill="currentColor"
						d="M17.874 5.91h1.077v3.305c0 .371-.09.696-.267.975a1.78 1.78 0 0 1-.738.65 2.5 2.5 0 0 1-1.106.232c-.423 0-.792-.077-1.109-.231a1.779 1.779 0 0 1-.738-.651 1.787 1.787 0 0 1-.263-.975V5.91h1.076v3.214c0 .194.042.366.127.517.086.151.207.27.363.356.155.086.337.129.544.129.209 0 .39-.043.544-.13a.908.908 0 0 0 .36-.355c.087-.15.13-.323.13-.517V5.91Zm1.768.887v-.888h4.181v.888h-1.558V11H21.2V6.797h-1.559Zm5.946-.888V11h-1.076V5.91h1.076ZM26.474 11V5.91h1.076v4.203h2.183V11h-3.26Zm5.048-5.09V11h-1.077V5.91h1.077Zm.691.887v-.888h4.181v.888h-1.558V11h-1.064V6.797h-1.559Zm4.572-.888h1.205l1.161 2.193h.05l1.16-2.193h1.207L39.71 9.2V11h-1.07V9.2l-1.856-3.29Z"
					/>
				</>
			);

			break;

		case "utility_large_text":
			iconColour = "bg-gradient-to-b from-gray-700 to-gray-900";
			ringColour = "ring-red-600";
			icon = (
				<>
					<path
						fill="currentColor"
						d="M17.874 5.91h1.077v3.305c0 .371-.09.696-.267.975a1.78 1.78 0 0 1-.738.65 2.5 2.5 0 0 1-1.106.232c-.423 0-.792-.077-1.109-.231a1.779 1.779 0 0 1-.738-.651 1.787 1.787 0 0 1-.263-.975V5.91h1.076v3.214c0 .194.042.366.127.517.086.151.207.27.363.356.155.086.337.129.544.129.209 0 .39-.043.544-.13a.908.908 0 0 0 .36-.355c.087-.15.13-.323.13-.517V5.91Zm1.768.887v-.888h4.181v.888h-1.558V11H21.2V6.797h-1.559Zm5.946-.888V11h-1.076V5.91h1.076ZM26.474 11V5.91h1.076v4.203h2.183V11h-3.26Zm5.048-5.09V11h-1.077V5.91h1.077Zm.691.887v-.888h4.181v.888h-1.558V11h-1.064V6.797h-1.559Zm4.572-.888h1.205l1.161 2.193h.05l1.16-2.193h1.207L39.71 9.2V11h-1.07V9.2l-1.856-3.29ZM7.434 26.477v-2.386h9.481v2.386h-3.281V35h-2.919v-8.523H7.434ZM18.554 35V24.09h7.863v2.387h-4.9v1.875h4.495v2.387h-4.495v1.875h4.879V35h-7.841Zm13.032-10.91 1.79 3.176h.085l1.81-3.175h3.303l-3.26 5.454L38.702 35h-3.388l-1.853-3.239h-.086L31.522 35h-3.367l3.345-5.454-3.238-5.455h3.324Zm8.492 2.387v-2.386h9.482v2.386h-3.282V35H43.36v-8.523h-3.28Z"
					/>
				</>
			);

			break;

		case "utility_speaker_timer":
			iconColour = "bg-gradient-to-b from-gray-700 to-gray-900";
			ringColour = "ring-red-600";
			icon = (
				<>
					<rect
						width="41"
						height="7"
						x="8"
						y="27"
						fill="currentColor"
						fillOpacity=".3"
						rx="1"
					/>
					<rect width="28" height="7" x="8" y="27" fill="currentColor" rx="1" />
					<path
						fill="currentColor"
						d="M17.874 5.91h1.077v3.305c0 .371-.09.696-.267.975a1.78 1.78 0 0 1-.738.65 2.5 2.5 0 0 1-1.106.232c-.423 0-.792-.077-1.109-.231a1.779 1.779 0 0 1-.738-.651 1.787 1.787 0 0 1-.263-.975V5.91h1.076v3.214c0 .194.042.366.127.517.086.151.207.27.363.356.155.086.337.129.544.129.209 0 .39-.043.544-.13a.908.908 0 0 0 .36-.355c.087-.15.13-.323.13-.517V5.91Zm1.768.887v-.888h4.181v.888h-1.558V11H21.2V6.797h-1.559Zm5.946-.888V11h-1.076V5.91h1.076ZM26.474 11V5.91h1.076v4.203h2.183V11h-3.26Zm5.048-5.09V11h-1.077V5.91h1.077Zm.691.887v-.888h4.181v.888h-1.558V11h-1.064V6.797h-1.559Zm4.572-.888h1.205l1.161 2.193h.05l1.16-2.193h1.207L39.71 9.2V11h-1.07V9.2l-1.856-3.29Z"
					/>
				</>
			);

			break;

		case "interaction_join":
			iconColour = "bg-gradient-to-r from-green-700 to-green-900";
			ringColour = "ring-green-600";
			icon = (
				<>
					<path d="M22.65 9.23h.914l.46.592.453.527.852 1.069h-1.004l-.587-.721-.3-.428-.788-1.039Zm2.756-.775c0 .555-.105 1.027-.315 1.416-.21.39-.494.687-.855.893-.36.204-.764.306-1.213.306a2.41 2.41 0 0 1-1.218-.309 2.195 2.195 0 0 1-.853-.892c-.209-.39-.313-.86-.313-1.414 0-.556.104-1.028.313-1.417.209-.39.493-.686.852-.89a2.41 2.41 0 0 1 1.219-.309c.449 0 .853.103 1.213.309.36.204.646.5.855.89.21.39.315.861.315 1.417Zm-1.09 0c0-.36-.055-.663-.162-.91a1.23 1.23 0 0 0-.45-.562 1.21 1.21 0 0 0-.681-.191c-.26 0-.488.063-.682.19a1.246 1.246 0 0 0-.452.563c-.106.247-.16.55-.16.91s.054.662.16.91c.108.246.259.434.452.561.194.128.421.191.681.191.26 0 .488-.063.682-.19a1.23 1.23 0 0 0 .45-.563c.107-.247.161-.55.161-.91Zm3.429 2.62c-.351 0-.655-.063-.91-.19a1.43 1.43 0 0 1-.589-.511 1.34 1.34 0 0 1-.204-.726c0-.2.04-.376.12-.53.079-.154.19-.296.332-.425.143-.13.309-.26.498-.39l.987-.649a.931.931 0 0 0 .28-.256.53.53 0 0 0 .09-.296.403.403 0 0 0-.122-.28c-.081-.087-.196-.13-.345-.127a.484.484 0 0 0-.43.238.462.462 0 0 0-.06.234c0 .106.03.214.09.323.06.11.139.225.238.348l.326.393L30.459 11h-1.103l-2.014-2.242c-.132-.151-.265-.308-.397-.472a2.741 2.741 0 0 1-.329-.527 1.417 1.417 0 0 1-.131-.61c0-.246.057-.468.171-.666.116-.197.279-.353.487-.467a1.48 1.48 0 0 1 .731-.174c.272 0 .506.055.704.167a1.164 1.164 0 0 1 .619 1.034c0 .24-.06.458-.18.653a1.8 1.8 0 0 1-.497.522l-1.039.749c-.14.102-.245.208-.313.315a.579.579 0 0 0-.01.632.63.63 0 0 0 .254.228c.11.055.238.082.385.082.18 0 .356-.04.53-.121.175-.083.334-.2.477-.35a1.753 1.753 0 0 0 .475-1.236h.877c0 .311-.034.592-.102.842a2.29 2.29 0 0 1-.283.652c-.121.184-.26.334-.415.452l-.145.09a9.467 9.467 0 0 0-.144.089 1.8 1.8 0 0 1-.634.33 2.443 2.443 0 0 1-.688.103ZM31.798 11h-1.153l1.757-5.09h1.387L35.544 11h-1.153l-1.276-3.928h-.04L31.799 11Zm-.072-2.001h2.724v.84h-2.724V9Z" fill="#fff"/><path d="M19.75 19.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5Zm0 9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5Zm9.75-9.75c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5Z" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M22.75 21.75h.75v.75h-.75v-.75Zm0 9.75h.75v.75h-.75v-.75Zm9.75-9.75h.75v.75h-.75v-.75Zm-3 6.75h.75v.75h-.75v-.75Zm0 6h.75v.75h-.75v-.75Zm6-6h.75v.75h-.75v-.75Zm0 6h.75v.75h-.75v-.75Zm-3-3h.75v.75h-.75v-.75Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
				</>
			);

			break;

		case "interaction_current_question":
			iconColour = "bg-gradient-to-r from-green-700 to-green-900";
			ringColour = "ring-green-600";
			icon = (
				<>
					<path d="M22.65 9.23h.914l.46.592.453.527.852 1.069h-1.004l-.587-.721-.3-.428-.788-1.039Zm2.756-.775c0 .555-.105 1.027-.315 1.416-.21.39-.494.687-.855.893-.36.204-.764.306-1.213.306a2.41 2.41 0 0 1-1.218-.309 2.195 2.195 0 0 1-.853-.892c-.209-.39-.313-.86-.313-1.414 0-.556.104-1.028.313-1.417.209-.39.493-.686.852-.89a2.41 2.41 0 0 1 1.219-.309c.449 0 .853.103 1.213.309.36.204.646.5.855.89.21.39.315.861.315 1.417Zm-1.09 0c0-.36-.055-.663-.162-.91a1.23 1.23 0 0 0-.45-.562 1.21 1.21 0 0 0-.681-.191c-.26 0-.488.063-.682.19a1.246 1.246 0 0 0-.452.563c-.106.247-.16.55-.16.91s.054.662.16.91c.108.246.259.434.452.561.194.128.421.191.681.191.26 0 .488-.063.682-.19a1.23 1.23 0 0 0 .45-.563c.107-.247.161-.55.161-.91Zm3.429 2.62c-.351 0-.655-.063-.91-.19a1.43 1.43 0 0 1-.589-.511 1.34 1.34 0 0 1-.204-.726c0-.2.04-.376.12-.53.079-.154.19-.296.332-.425.143-.13.309-.26.498-.39l.987-.649a.931.931 0 0 0 .28-.256.53.53 0 0 0 .09-.296.403.403 0 0 0-.122-.28c-.081-.087-.196-.13-.345-.127a.484.484 0 0 0-.43.238.462.462 0 0 0-.06.234c0 .106.03.214.09.323.06.11.139.225.238.348l.326.393L30.459 11h-1.103l-2.014-2.242c-.132-.151-.265-.308-.397-.472a2.741 2.741 0 0 1-.329-.527 1.417 1.417 0 0 1-.131-.61c0-.246.057-.468.171-.666.116-.197.279-.353.487-.467a1.48 1.48 0 0 1 .731-.174c.272 0 .506.055.704.167a1.164 1.164 0 0 1 .619 1.034c0 .24-.06.458-.18.653a1.8 1.8 0 0 1-.497.522l-1.039.749c-.14.102-.245.208-.313.315a.579.579 0 0 0-.01.632.63.63 0 0 0 .254.228c.11.055.238.082.385.082.18 0 .356-.04.53-.121.175-.083.334-.2.477-.35a1.753 1.753 0 0 0 .475-1.236h.877c0 .311-.034.592-.102.842a2.29 2.29 0 0 1-.283.652c-.121.184-.26.334-.415.452l-.145.09a9.467 9.467 0 0 0-.144.089 1.8 1.8 0 0 1-.634.33 2.443 2.443 0 0 1-.688.103ZM31.798 11h-1.153l1.757-5.09h1.387L35.544 11h-1.153l-1.276-3.928h-.04L31.799 11Zm-.072-2.001h2.724v.84h-2.724V9Z" fill="#fff"/><path d="M25.879 22.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M37 27a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H28v-.008Z"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</>
			);

			break;

		case "custom_html":
			iconColour = "bg-gray-900";
			ringColour = "ring-gray-600";
			icon = (
				<>
				<path d="M36.5 15.727 45 24l-8.5 8.273m-17 0L11 24l8.5-8.273M31.643 11l-7.286 26" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
				</>
			);

			break;

		case "progress":
			iconColour = "bg-gradient-to-r from-indigo-700 to-indigo-900";
			ringColour = "ring-gray-600";
			icon = (
				<>
					<rect x="7.5" y="32.5" width="42" height="5" rx="2.5" stroke="currentColor"/><rect x="7" y="32" width="31" height="6" rx="3" fill="currentColor"/>
				</>
			);

			break;

		case "folder_add":
			iconColour = "text-gray-600";
			icon = (
				<path
					d="M23.25 25.25h7.5-7.5zM27 21.5V29v-7.5zm-11.25 8.75v-12.5a2.5 2.5 0 012.5-2.5h7.5l2.5 2.5h7.5a2.5 2.5 0 012.5 2.5v10a2.5 2.5 0 01-2.5 2.5h-17.5a2.5 2.5 0 01-2.5-2.5z"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "folder_remove":
			iconColour = "text-gray-300";
			icon = (
				<path
					d="M23.25 25.25h7.5-7.5zm-7.5 5v-12.5a2.5 2.5 0 012.5-2.5h7.5l2.5 2.5h7.5a2.5 2.5 0 012.5 2.5v10a2.5 2.5 0 01-2.5 2.5h-17.5a2.5 2.5 0 01-2.5-2.5z"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			);
			break;

		case "move":
			iconColour = "text-gray-600";
			icon = (
				<>
					<rect
						x="14"
						y="22.544"
						width="27.105"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
					<rect
						x="14"
						y="14"
						width="27.105"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
					<rect
						x="14"
						y="30.312"
						width="27.105"
						height="3.696"
						rx="1.848"
						fill="currentColor"
					/>
				</>
			);
			break;

		default:
			iconColour = "text-gray-600";
			icon = "";
			break;
	}

	return { icon, iconColour, ringColour };
};

export default function Cue({
	cue = {},
	cues = {},
	index = 0,
	hidden = false,
	cueToEdit = null,
	onCueEdit = null,
	clickable = true,
	cloud = false,
	order = [],
	defaultThemeId = "",
}) {
	const { show } = useContextMenu({
		id: cue.id,
	});
	const [cuesLinkedToThisCue, setCuesLinkedToThisCue] = useState(false);
	const isPro = useIsPro();
	const locked = useSelector((state) => state.rundown?.locked || false);

	const { projectId } = useParams();
	const projectDefaultTheme = useSelector(
		(state) => state.main?.projects[projectId]?.theme
	);
	// const cues = useSelector((state) => state.main?.projects?.[projectId]?.cues);

	const dynamicLists = useSelector(
		(state) => state.main?.projects?.[projectId].dynamicLists
	);

	let copyDataTextRef = useRef();
	let copyTextRef = useRef();

	const handleItemClick = (
		{ event, props, triggerEvent, data },
		variableText
	) => {
		const copyToClipboard = (textToCopy) => {
			copyTextRef.current.value = textToCopy;
			copyTextRef.current?.focus();
			copyTextRef.current?.select();
			document.execCommand("copy");
		};

		const handleDuplicateCue = async () => {
			const res = await cuesApi.addCue(
				cue.type,
				projectId,
				{
					...cue,
					status: "ready",
				},
				false,
				defaultThemeId
			);

			await cuesApi.reorderCue(
				projectId,
				res.data.id,
				cues.length,
				index + 1,
				order
			);

			return onCueEdit(res.data);
		};

		switch (event.currentTarget.id) {
			case "edit":
				onCueEdit(cue);
				break;
			case "duplicate":
				handleDuplicateCue();
				break;
			case "variable_copy_text":
				copyToClipboard(`[${cue.id}.${variableText}]`);
				break;
			case "api_copy_show":
				copyToClipboard(`graphic/${cue.id}/show`);
				break;
			case "api_copy_hide":
				copyToClipboard(`graphic/${cue.id}/hide`);
				break;
			case "api_copy_update":
				copyToClipboard(`graphic/${cue.id}/update`);
				break;
			case "api_copy_json":
				copyToClipboard(definitions[cue.type]?.updateJson);
				break;
			case "delete":
				cuesApi.removeCue(cue.id, projectId);
				break;
			default:
				break;
		}
	};

	const showContextMenu = (e) => {
		// put whatever custom logic you need
		// you can even decide to not display the Menu
		show(e);
	};

	let XYZ =
		parseInt(cue.scale) < 100 ||
		parseInt(cue.scale) > 100 ||
		parseInt(cue.offsetX) < 0 ||
		parseInt(cue.offsetX) > 0 ||
		parseInt(cue.offsetY) < 0 ||
		parseInt(cue.offsetY) > 0;

	let bgColour;
	let bgColourHover;
	let borderColour;
	let statusColour;
	let statusBorderColour;

	switch (cue.status) {
		case "ready":
			bgColour = "gray-800";
			borderColour = "gray-800";
			bgColourHover = "gray-700";
			statusColour = "bg-gray-800";
			statusBorderColour = "gray-800";
			break;

		case "cued":
			bgColour = "gray-800";
			borderColour = "gray-800";
			bgColourHover = "gray-700";
			statusColour = "bg-gray-800";
			statusBorderColour = "red-600 border-dashed";
			break;

		case "coming":
			bgColour = "gray-700";
			borderColour = "gray-700";
			bgColourHover = "gray-600";
			statusColour = "bg-gray-800";
			statusBorderColour = "red-600";
			break;

		case "onair":
			bgColour = "gray-800";
			borderColour = "gray-800";
			bgColourHover = "gray-700";
			// bgColour = "gray-700";
			// bgColourHover = "bg-gray-600";
			statusColour = "bg-red-500";
			statusBorderColour = "red-500";
			break;

		case "cuedoff":
			bgColour = "gray-700";
			borderColour = "gray-700";
			bgColourHover = "gray-600";
			statusColour = "bg-red-500";
			statusBorderColour = "gray-900  border-dashed";
			break;

		case "going":
			bgColour = "gray-700";
			borderColour = "gray-700";
			bgColourHover = "gray-600";
			statusColour = "bg-gray-800";
			statusBorderColour = "red-500";
			break;

		case "offair":
			bgColour = "gray-800";
			borderColour = "gray-800";
			bgColourHover = "gray-700";
			statusColour = "bg-gray-800";
			statusBorderColour = "gray-800";
			break;

		default:
			bgColour = "gray-800";
			borderColour = "gray-800";
			bgColourHover = "gray-800";
			statusColour = "bg-gray-800";
			statusBorderColour = "gray-800";
			break;
	}

	useEffect(() => {
		// CLOUD FIX
		if (!cloud) {
			let total = cues.filter((c) => c.linkedTo === cue.id);

			if (total.length > 0) {
				setCuesLinkedToThisCue(true);
			} else {
				setCuesLinkedToThisCue(false);
			}
		}
	}, [cues]);

	return (
		<Draggable draggableId={cue.id} index={index} isDragDisabled={locked}>
			{(provided) => (
				<div
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					ref={provided.innerRef}
					className={`flex pr-2 w-full 
						${hidden && 'hidden'}
     transition duration-500 ease-in-out focus:outline-none
     ${cue.type === "section" && "mt-6 mb-2"}
     ${cue.type !== "section" || cue.type !== "group" ? "my-2" : "my-0.5"}

     `}
				>
					{/* {cue.type === 'section' && 
						<button
							className="whitespace-nowrap w-10 focus:outline-none focus:ring rounded"
							onClick={()=>{
								const newState = cue.hidden ? false : true
								cuesApi.updateCue(cue.id, projectId, { hidden: newState })
							}}
						>
								<Tooltip>
									<TooltipTrigger>
										<FontAwesomeIcon
											icon={faChevronDown}
											className={[
												"text-gray-500 hover:text-gray-300 transition-all",
												cue.hidden ? '-rotate-90' : 'rotate-0' 
											].join(' ')}
										/>
									</TooltipTrigger>
									<TooltipContent>
										<p className="font-semibold">{cue.hidden ? 'Expand section' : 'Collapse section'}</p>
										<p className="text-xs">This will show/hide all graphics below this section.</p>
									</TooltipContent>
								</Tooltip>
								</button>
					} */}
					{/* CUE STATUS */}
					<CueStatus cue={cue} clickable={clickable} />

					{/* RIGHT CLICK MENU */}
					<div className="absolute top-0 left-0">
						<Menu id={cue.id} theme="dark" animation="fade">
							<Item id="edit" onClick={handleItemClick}>
								Edit
							</Item>
							{!cloud && (
								<>
									<Item id="duplicate" onClick={handleItemClick}>
										Duplicate
									</Item>

									{definitions[cue.type]?.internalData && (
										<Submenu label="Variables" arrow="→" disabled={!clickable}>
											{definitions[cue.type]?.internalData?.map((item, index) => {
												const entries = Object.entries(item)[0];

												return (
													<Item
														key={index}
														id="variable_copy_text"
														onClick={(e) => handleItemClick(e, entries[1])}
													>
														Copy "{entries[0]}" variable
													</Item>
												);
											})}
											<input
												className="opaicty-0 h-0 absolute"
												type="text"
												ref={copyDataTextRef}
											/>
										</Submenu>
									)}

									<Submenu label="API" arrow="→" disabled={!clickable}>
										<Item id="api_copy_show" onClick={handleItemClick}>
											Copy "Show" URI
										</Item>
										<Item id="api_copy_hide" onClick={handleItemClick}>
											Copy "Hide" URI
										</Item>
										<Separator />
										<Item id="api_copy_update" onClick={handleItemClick}>
											Copy "Update" URI
										</Item>
										<Item
											disabled={!definitions[cue.type]?.updateJson}
											id="api_copy_json"
											onClick={handleItemClick}
										>
											Copy body JSON
										</Item>
										<input
											className="opaicty-0 h-0 absolute"
											type="text"
											ref={copyTextRef}
										/>
									</Submenu>
								</>
							)}
							<Separator />
							<Item
								id="delete"
								disabled={cue.status === "onair"}
								onClick={handleItemClick}
							>
								Delete
							</Item>
						</Menu>
					</div>
					
					{/* ACTUAL CUE */}
					<button
					// onClick={()=>onCueEdit(cue)}
						title="Toggle on/off air"
						onClick={() => {

							// Update hidden status if cue is a section

							if(cue.type === 'section') {
								const newStatus = cue.hidden ? false : true
								return cuesApi.updateCue(cue.id, projectId, {
									hidden: newStatus,
								});
							}
							let changeTo;

							if (cue.status === "onair" || cue.status === "cuedoff") {
								changeTo = "going";
							} else if (cue.status === "coming" || cue.status === "going") {
								changeTo = "ready";
							} else {
								changeTo = "coming";
							}

							cuesApi.updateCue(cue.id, projectId, {
								status: changeTo,
							});
						}}
						className={`ml-3 relative w-full  rounded-tl rounded-bl flex justify-between focus:outline-none  hover:bg-${
							clickable ? bgColourHover : "gray-900"
						} transition duration-500 ease-in-out
            			${clickable && `border-2 focus:bg-${bgColourHover}`}
						${
							cueToEdit && cueToEdit.id === cue.id
								? "border-green-700 border-r-0"
								: "border-gray-800"
						}
									${locked && "rounded-tr rounded-br"}
						${
							cue.status === "offair" || !clickable
								? `bg-gray-900 border-${borderColour} hover:border-${bgColourHover}`
								: `bg-${bgColour}`
						}
						
						${!["", "none", null, undefined].includes(cue.linkedTo) && "opacity-75"}
      
        `}
						onContextMenu={showContextMenu}
					>
						<div
							className={`flex w-full rounded
                      ${cue.type === "section" && "h-8 border-none"}
                      
      `}
						>
							{cue.type !== 'section' && <span
								className={`flex items-center justify-center rounded w-14 ${
									cueDesign(cue.type).iconColour
								}`}
							>
								<svg
									width="56"
									height="48"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									{cueDesign(cue.type).icon}
								</svg>
							</span>}
							<div
								className={`flex w-full pl-1 pr-2 text-left justify-between items-center
                      ${
												cue.type === "section" ||
												!["", "none", null, undefined].includes(cue.linkedTo)
													? "pt-0 pb-0"
													: " pt-1 pb-2"
											}
       `}
							>
								{/* <span className="px-1 text-xxs uppercase leading-3 tracking-wide font-semibold text-gray-500 bg-gray-900 rounded w-min whitespace-nowrap">
									{definitions[cue.type].name}
								</span> */}
								<div className="flex flex-col items-left pt-1 leading-1 ml-2 mr-3">
									{cue.type !== "section" && (
										<span className="flex items-center w-min whitespace-pre justify-center text-center rounded uppercase text-xxs opacity-50 text-gray-400">
											{!locked && (
												<span className="text-gray-100">
													ID: <span className="font-semibold">{cue.id}</span> •{" "}
												</span>
											)}
											{cue.label ? (
												<span className="font-bold text-gray-100">
													{cue.label}
												</span>
											) : (
												definitions[cue.type]?.name
											)}
											{cue.type === "big_time" && `• ${cue.shape}`}
											{cue.type === "lower_third_animated" &&
												` • ${cue.animationName}`}
											{cue.onAirDuration && (
												<span className="lowercase">
													∙ {cue.onAirDuration}s
												</span>
											)}
											{cue.type === "video" && cue.loop === true && `• Looping`}
											{cue.type === "time_countdown" && (
												<span className="lowercase">
													{" "}
													• {msToString(cue.duration)}
												</span>
											)}
										</span>
									)}
									<CueContents cue={cue} />
								</div>
								<div>
									{cue.onAirDuration ? <IconTrigger /> : null}
									{cue.theme && !cloud && cue.theme !== projectDefaultTheme ? (
										<IconT />
									) : null}
									{XYZ ? <IconXYZ /> : null}
									{["", "none", null, undefined].includes(cue.linkedTo) ? (
										<IconLinked
											hidden={
												!isPro || cue.type === "section" || !cuesLinkedToThisCue
											}
											bg={stc(cue.id)}
											border={stc(cue.id)}
										/>
									) : (
										<IconLinked
											hidden={!isPro || cue.type === "section"}
											bg="transparent"
											border={stc(cue.linkedTo)}
										/>
									)}
									{isPro && <Outputs cue={cue} />}
								</div>
							</div>
						</div>
					</button>


					{/* Controls */}
					<div>
						{(containsDataSourceList(cue.line_one) ||
							containsDataSourceList(cue.line_two)) && (
							<div className=" flex flex-col space-y-0.5 ml-1 h-100">
								<H2RButton
									text="↑"
									colour="dark"
									size="small"
									onClick={() => {
										const match = containsDataSourceList(cue.line_one)
										const listNumber = extractNumberFromList(match)
										const currentlySelected = dynamicLists[listNumber][0][0].selected;
										cuesApi.updateDynamicListSelectedRow(
											projectId,
											listNumber,
											currentlySelected - 1
										);
									}}
								/>
								<H2RButton
									text="↓"
									colour="dark"
									size="small"
									onClick={() => {
										const match = containsDataSourceList(cue.line_one)
										const listNumber = extractNumberFromList(match)
										const currentlySelected = dynamicLists[listNumber][0][0].selected;
										cuesApi.updateDynamicListSelectedRow(
											projectId,
											listNumber,
											currentlySelected + 1
										);
									}}
								/>
							</div>
						)}
						{cue.type === "lyrics" && (
							<div className=" flex flex-col space-y-0.5 ml-1 h-100">
								<H2RButton
									text="↑"
									colour="dark"
									size="small"
									disabled={cue.currentLyric <= 0}
									onClick={() => {
										const currentlySelected = cue.currentLyric;
										cuesApi.updateCue(cue.id, projectId, {
											currentLyric: currentlySelected - 1,
											current_lyric_text: cue.lyrics?.[currentlySelected - 1],
										});
									}}
								/>
								<H2RButton
									text="↓"
									colour="dark"
									size="small"
									disabled={cue.currentLyric === cue.lyrics?.length - 1}
									onClick={() => {
										const currentlySelected = cue.currentLyric;
										cuesApi.updateCue(cue.id, projectId, {
											currentLyric: currentlySelected + 1,
											current_lyric_text: cue.lyrics?.[currentlySelected + 1],
										});
									}}
								/>
							</div>
						)}

						{/* PROGRESS BUTTONS */}
						{[
							"progress",
						].includes(cue.type) && (
							<div className="flex ml-2">
								<div className="flex flex-col">
									<H2RButton
										icon={faArrowUp}
										size="small"
										type="link"
										colour="light"
										disabled={parseInt(cue.percent) >= 100}
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {percent: parseInt(cue.percent) + 1})
										}
									/>
									<H2RButton
										icon={faArrowDown}
										size="small"
										type="link"
										colour="light"
										disabled={parseInt(cue.percent) <= 0}
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {percent: parseInt(cue.percent) - 1})
										}
									/>
								</div>
							</div>
						)}
						{/* TIME COUNTDOWN BUTTONS */}
						{[
							"time_countdown",
							"big_time_countdown",
							"utility_speaker_timer",
						].includes(cue.type) && (
							<div className="flex ml-2">
								{cue.state === "running" && (
									<div className="flex flex-col mr-2">
										<H2RButton
											text="-1m"
											size="small"
											type="link"
											colour="light"
											css="!w-10"
											onClick={() =>
												timersApi.countdownSubtract(
													cue.id,
													projectId,
													60000,
													cue.endAt
												)
											}
											options={[
												{
													label: '-1s',
													value: 1000
												},
												{
													label: '-10s',
													value: 10000
												},
												{
													label: '-1m',
													value: 60000
												},
												{
													label: '-5m',
													value: 300000
												},
												{
													label: '-10m',
													value: 600000
												}
											]}
											optionClicked={(value) => {
												timersApi.countdownSubtract(
													cue.id,
													projectId,
													value,
													cue.endAt
												)
											}}
										/>
										<H2RButton
											text="+1m"
											size="small"
											type="link"
											colour="light"
											css="!w-10"
											onClick={() =>
												timersApi.countdownSubtract(
													cue.id,
													projectId,
													-60000,
													cue.endAt
												)
											}
											options={[
												{
													label: '+1s',
													value: 1000
												},
												{
													label: '+10s',
													value: 10000
												},
												{
													label: '+1m',
													value: 60000
												},
												{
													label: '+5m',
													value: 300000
												},
												{
													label: '+10m',
													value: 600000
												}
											]}
											optionClicked={(value) => {
												timersApi.countdownSubtract(
													cue.id,
													projectId,
													-value,
													cue.endAt
												)
											}}
										/>
									</div>
								)}
								<div className="flex flex-col">
									<H2RButton
										icon={faRefresh}
										size="small"
										type="link"
										colour="light"
										onClick={() =>
											timersApi.countdownReset(cue.id, projectId, cue.duration)
										}
									/>
									{!["paused", "reset"].includes(cue.state) && (
										<H2RButton
											icon={faPause}
											size="small"
											colour="dark"
											onClick={() =>
												timersApi.countdownPause(cue.id, projectId, cue.endAt)
											}
										/>
									)}
									{cue.state !== "running" && (
										<H2RButton
											icon={faPlay}
											size="small"
											onClick={() =>
												timersApi.countdownRun(
													cue.id,
													projectId,
													cue.pausedTimeLeft,
													cue.duration
												)
											}
										/>
									)}
								</div>
							</div>
						)}
						{/* TIME COUNTUP BUTTONS */}
						{["time_countup", "big_time_countup"].includes(cue.type) && (
							<div className="flex flex-col ml-2">
								<H2RButton
									icon={faRefresh}
									size="small"
									type="link"
									colour="light"
									onClick={() =>
										cuesApi.updateCue(cue.id, projectId, {
											state: "reset",
											startedAt:
												Date.now() - (stringToMS(cue.startOffset) || 0),
											pausedTimeElapsed: 0,
										})
									}
								/>
								{!["paused", "reset"].includes(cue.state) && (
									<H2RButton
										icon={faPause}
										size="small"
										colour="dark"
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {
												state: "paused",
												pausedTimeElapsed: Date.now() - parseInt(cue.startedAt),
											})
										}
									/>
								)}
								{cue.state !== "running" && (
									<H2RButton
										icon={faPlay}
										size="small"
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {
												state: "running",
												startedAt:
													cue.pausedTimeElapsed > 0
														? Date.now() - cue.pausedTimeElapsed
														: Date.now() - (stringToMS(cue.startOffset) || 0),
												pausedTimeElapsed: 0,
											})
										}
									/>
								)}
							</div>
						)}
						{/* NOW NEXT THEN */}
						{cue.type === "now_next_then" && cue.loop === "manual" && (
							<div className="flex ml-2">
								<H2RButton
									text="1"
									size="small"
									colour={cue.goTo === 0 ? "dark" : "light"}
									onClick={() =>
										cuesApi.updateCue(cue.id, projectId, {
											goTo: 0,
										})
									}
								/>
								{["2", "3"].includes(cue.count) && (
									<H2RButton
										text="2"
										size="small"
										colour={cue.goTo === 1 ? "dark" : "light"}
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {
												goTo: 1,
											})
										}
									/>
								)}
								{cue.count === "3" && (
									<H2RButton
										text="3"
										size="small"
										colour={cue.goTo === 2 ? "dark" : "light"}
										onClick={() =>
											cuesApi.updateCue(cue.id, projectId, {
												goTo: 2,
											})
										}
									/>
								)}
							</div>
						)}
						{/* SLIDESHOW */}
						{cue.type === "slideshow" && cue.loop === "manual" && (
							<div className="flex flex-col ml-2">
								<H2RButton
									text=""
									icon={faArrowLeft}
									size="small"
									disabled={cue.goTo === 0}
									colour={"dark"}
									onClick={() =>
										cuesApi.updateCue(cue.id, projectId, {
											goTo: cue.goTo - 1 || 0,
										})
									}
								/>
								<H2RButton
									text=""
									icon={faArrowRight}
									size="small"
									colour={"dark"}
									disabled={cue.goTo === cue.items.length - 1}
									onClick={() =>
										cuesApi.updateCue(cue.id, projectId, {
											goTo: cue.goTo + 1 || 1,
										})
									}
								/>
							</div>
						)}

					</div>

					{/* Edit, drag and delete */}
					{!locked && (
						<>
							<span
								className={`flex justify-center items-center cursor-grab ml-1 px-2 md:px-4 w-10 md:w-14 bg-gray-800 hover:bg-gray-700 focus:bg-gray-700  transition duration-500 ease-in-out ${
									cueDesign("move").iconColour
								}
								${
									cueToEdit && cueToEdit.id === cue.id
										? "border-2 border-green-700 border-l-0 border-r-0"
										: "border-gray-800"
								}
								`}
							>
								<svg
									width="25"
									height="24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<rect
										x="1.02"
										y="10.69"
										width="22.98"
										height="3.326"
										rx="1.663"
										fill="currentColor"
									/>
									<rect
										x="1"
										y="5"
										width="22.98"
										height="3.326"
										rx="1.663"
										fill="currentColor"
									/>
									<rect
										x="1"
										y="16"
										width="22.98"
										height="3.326"
										rx="1.663"
										fill="currentColor"
									/>
								</svg>
							</span>

							<button
								title="Edit graphic"
								className={`text-gray-500 px-2 md:px-4 w-10 md:w-14 focus:outline-none hover:bg-green-800 focus:bg-green-700 transition duration-500 ease-in-out ${
									cueToEdit && cueToEdit.id === cue.id
										? "bg-green-700 text-green-100"
										: "bg-gray-800 text-green-500"
								}`}
								onClick={() => {
									onCueEdit(cue);
								}}
							>
								<svg
									width="25"
									height="24"
									stroke="currentColor"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M14.963 5.796l3.241 3.241-3.241-3.241zm1.375-1.375a2.292 2.292 0 113.241 3.241l-12.62 12.62H3.75V17.01L16.338 4.42v0z"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
							<button
								edit="Delete graphic"
								className={`text-gray-500 px-2 md:px-4 w-10 md:w-14 bg-gray-800 rounded-br rounded-tr focus:outline-none hover:bg-red-800 focus:bg-gray-700 transition duration-500 ease-in-out
								
								${
									cueToEdit && cueToEdit.id === cue.id
										? "border-2 border-green-700 border-l-0"
										: "border-gray-800"
								}
								${
									(cue.status === "onair" ||
										cue.status === "coming" ||
										cue.status === "going") &&
									"opacity-50 cursor-not-allowed"
								}
								`}
								onClick={() => {
									return cuesApi.removeCue(cue.id, projectId);
								}}
								disabled={cue.status === "onair"}
								title={
									cue.status === "onair" ? "Cannot delete an onair graphic" : ""
								}
							>
								<svg
									width="25"
									height="24"
									className="text-red-500"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M4.833 6.75h15.334m-.959 0l-.83 11.636a1.916 1.916 0 01-1.913 1.78h-7.93a1.917 1.917 0 01-1.913-1.78L5.792 6.75h13.416zm-8.625 3.833v5.75-5.75zm3.834 0v5.75-5.75zm.958-3.833V3.875a.958.958 0 00-.958-.958h-3.834a.958.958 0 00-.958.958V6.75h5.75z"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						</>
					)}

				</div>
			)}
		</Draggable>
	);
}

export const CueContents = ({ cue, twoLines = false }) => {
	let { projectId } = useParams();
	const dynamicText = useSelector(
		(state) => state.main?.projects?.[projectId]?.dynamicText
	);
	const dynamicLists = useSelector(
		(state) => state.main?.projects?.[projectId]?.dynamicLists
	);

	const CueContentsMemo = useMemo(
		() => definitions?.[cue.type]?.components?.GraphicCueContent,
		[cue]
	);

	if(!CueContentsMemo) return null

	return (
		<CueContentsMemo
			cue={cue}
			twoLines={twoLines}
			dynamicText={dynamicText}
			dynamicLists={dynamicLists}
		/>
	);
};

const Outputs = ({ cue }) => {
	if (cue.type === "section") return null;
	return (
		<>
			{<OutputNumber enabled={cue.outputOne || cue.outputOne === undefined} />}
			{<OutputNumber number="2" enabled={cue.outputTwo} />}
			{<OutputNumber number="3" enabled={cue.outputThree} />}
			{<OutputNumber number="4" enabled={cue.outputFour} />}
			{<OutputNumber number="U" enabled={cue.outputUtility} />}
		</>
	);
};

export const SocialCue = ({
	size = "small",
	cue,
	dataId,
	chatMessage,
	moderator = false,
	listable = true,
	activeChatMessage = null,
}) => {
	const [mention, setMention] = useState(false);

	const { projectId } = useParams();
	const cues = useSelector((state) => state.main.projects[projectId].cues);
	const channelUserName = useSelector(
		(state) => state.main.settings?.youtube_channel_name
	);
	const settings = useSelector((state) => state.main.settings);

	useEffect(() => {
		if (chatMessage?.snippet?.displayMessage?.includes(channelUserName)) {
			return setMention(true);
		}
	}, [channelUserName, chatMessage.message]);

	return (
		<div
			className={`flex mb-2 mr-2 rounded
  ${mention && "border-2 border-green-600 "}
  ${
		chatMessage?.snippet?.type === "superChatEvent" &&
		"border-2 border-main-600"
	}
  ${
		chatMessage?.snippet?.type === "newSponsorEvent" &&
		"border-2 border-main-600"
	}
  
  `}
		>
			<button
				onClick={async () => {
					if (!listable)
						return cuesApi.updateCue(cue.id, projectId, { status: "going" });

					let cuesFiltered = cues.filter((c) => c.type === "social");

					if (cuesFiltered.length > 0) {
						return cuesFiltered.map((c, i) => {
							if (c.chat.id === chatMessage.id && c.status === "offair") {
								return cuesApi.updateCue(c.id, projectId, { status: "coming" });
							} else if (c.chat.id === chatMessage.id && c.status === "onair") {
								return cuesApi.updateCue(c.id, projectId, { status: "going" });
							}

							cuesApi.updateCue(c.id, projectId, { status: "ready" });

							cuesApi.listDataSourceMessage(dataId, chatMessage.id, {
								shown: true,
							});

							setTimeout(() => {
								if (i === 0) {
									cuesApi.updateCue(c.id, projectId, {
										chat: {
											...chatMessage,
											snippet: {
												...chatMessage.snippet,
												displayMessage: 
													chatMessage.snippet.type === "superChatEvent"
														? `${
																chatMessage.snippet?.superChatDetails
																	?.amountDisplayString
														  } ${
																chatMessage.snippet?.superChatDetails
																	?.userComment || ""
														  }`
														: chatMessage?.snippet?.displayMessage
												,

												textMessageDetails: {
													...chatMessage.snippet.textMessageDetails,

													messageText:
														chatMessage.snippet?.textMessageDetails?.messageText
													,
												},
											},
											textMessageDetails: {
												messageText:
													chatMessage?.snippet?.textMessageDetails?.messageText
												,
											},
											authorDetails: {
												...chatMessage.authorDetails,
												profileImageUrl:
													chatMessage.authorDetails?.profileImageUrl
												,
												displayName: 
													chatMessage.authorDetails?.displayName
												,
											},
										},
										status: "coming",
									});
								}
							}, 50);
						});
					} else {
						await cuesApi.addCue(
							"social",
							projectId,
							{
								chat: {
									...chatMessage,
									snippet: {
										...chatMessage.snippet,
										displayMessage: 
											chatMessage.snippet.type === "superChatEvent"
												? `${
														chatMessage.snippet?.superChatDetails
															?.amountDisplayString
												  } ${
														chatMessage.snippet?.superChatDetails
															?.userComment || ""
												  }`
												: chatMessage.snippet.displayMessage
										,
									},
									textMessageDetails: {
										...chatMessage.snippet.textMessageDetails,
										messageText: 
											chatMessage.snippet?.textMessageDetails?.messageText
										,
									},
									authorDetails: {
										...chatMessage.authorDetails,
										profileImageUrl: 
											chatMessage.authorDetails?.profileImageUrl
										,
										displayName: 
											chatMessage.authorDetails?.displayName
										,
									},
								},
							},
							true
						);


						await cuesApi.listDataSourceMessage(dataId, chatMessage.id, {
							shown: true,
						});
					}
				}}
				disabled={moderator}
				className={`z-10 px-3 py-2 border-2 text-left w-full rounded-tl rounded-bl flex items-center focus:outline-none transition duration-500 ease-in-out
						
						${!listable && "rounded-tr rounded-br"}
						${
							chatMessage.shown
								? "bg-gray-900 focus:bg-gray-800 hover:bg-gray-800"
								: "bg-gray-800 focus:bg-gray-700 hover:bg-gray-700"
						}
						${moderator && "cursor-default"}
						${
							activeChatMessage?.chat?.id === chatMessage.id
								? " border-main-500"
								: "border-transparent"
						}

					`}
			>

				{/* USER IMAGE */}
				<div
					className={`bg-cover bg-center rounded-full relative
								${size === "small" && "h-10 w-10"}
								${size === "large" && "h-14 w-14"}
								${size === "xl" && "h-24 w-20"}
					`}
					style={{
						backgroundImage: `url(${chatMessage?.authorDetails?.profileImageUrl})`,
					}}
				>
					{/* PLATFORM IMAGE OR NAME */}
					{chatMessage.platform && (
						<div
							className={`bg-cover bg-center rounded-full mr-2 absolute -bottom-2 -left-2
								${size === "small" && "h-5 w-5"}
								${size === "large" && "h-8 w-8"}
								${size === "xl" && "h-10 w-10"}
					`}
							style={{
								backgroundImage: `url(${chatMessage?.platform?.logoUrl})`,
							}}
						></div>
					)}
				</div>

				{/* AUTHOR NAME */}
				<div
					style={{ wordBreak: "break-word" }}
					className={`w-2/12 ml-4 text-gray-300 overflow-ellipsis
							${size === "small" && "text-xs"}
							${size === "large" && "text-sm"}
							${size === "xl" && "text-3xl"}
					`}
				>
					{chatMessage?.authorDetails?.isChatSponsor && (
						<span className="whitespace-pre text-xxs font-semibold bg-green-800 px-1 rounded uppercase text-green-500">
							{settings?.youtube_member_language || "Member"}
						</span>
					)}{" "}
					{chatMessage?.authorDetails?.displayName}
				</div>

				{/* CHAT MESSAGE */}
				<div
					style={{ wordBreak: "break-word" }}
					className={`w-8/12 ml-4 
							${mention ? "text-gray-100 font-semibold" : "text-gray-300"}
							${size === "small" && "text-sm"}
							${size === "large" && "text-lg"}
							${size === "xl" && "text-3xl"}
			
			`}
				>
					{chatMessage?.snippet?.displayMessage}
				</div>
			</button>
			{listable && (
				<button
					className={`px-2 text-gray-500  rounded-br rounded-tr focus:outline-none transition duration-500 ease-in-out
						${
							chatMessage.listed
								? "bg-green-600 hover:bg-green-500 focus:bg-green-700"
								: "bg-gray-800 hover:bg-gray-700 focus:bg-gray-700"
						}
						${chatMessage.shown && !chatMessage.listed && "opacity-25"}
						
			`}
					onClick={() => {
						let boolean = chatMessage.listed ? !chatMessage.listed : true;
						cuesApi.listDataSourceMessage(dataId, chatMessage.id, {
							listed: boolean,
						});
					}}
				>
					<span
						className={`rounded w-14 ${
							cueDesign(chatMessage.listed ? "folder_remove" : "folder_add")
								.iconColour
						}`}
					>
						<svg
							width="56"
							height="48"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							{
								cueDesign(chatMessage.listed ? "folder_remove" : "folder_add")
									.icon
							}
						</svg>
					</span>
				</button>
			)}
		</div>
	);
};

export const OutputNumber = ({ number = "1", enabled, opacity = "50" }) => {
	return (
		<span
			className={`text-right ml-0.5 px-1 py-0.5 rounded uppercase text-xs text-gray-200 border 
						opacity-${opacity} transition-opacity duration-200
						${enabled ? "border-red-800  bg-red-700" : "border-gray-800  bg-gray-800"}
		`}
		>
			<span>{number}</span>
		</span>
	);
};

export const IconXYZ = ({ opacity = "50" }) => {
	return (
		<span
			className={`text-right ml-1 px-1 py-1 rounded uppercase text-xs text-gray-200 border border-gray-900 
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span>XYZ</span>
		</span>
	);
};

export const IconT = ({ opacity = "50" }) => {
	return (
		<span
			className={`text-right ml-1 px-1 py-1 rounded uppercase text-xs text-gray-200 border border-gray-900
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span>THM</span>
		</span>
	);
};

export const IconContent = ({ opacity = "100" }) => {
	return (
		<span
			className={`text-right ml-1 px-1 py-1 rounded uppercase text-xs text-gray-400 border border-gray-900
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span>Abc</span>
		</span>
	);
};

export const IconTrigger = ({ opacity = "50" }) => {
	return (
		<span
			className={`text-right ml-1 px-1 py-1 rounded uppercase text-xs text-gray-200 border border-gray-900
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span>TRG</span>
		</span>
	);
};

export const IconCrop = ({ opacity = "50" }) => {
	return (
		<span
			className={`text-right ml-1 px-1 py-1 rounded uppercase text-xs text-gray-200 border border-gray-900
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span>CRP</span>
		</span>
	);
};

export const IconLinked = ({
	hidden = true,
	opacity = "100",
	bg = "transparent",
	border = "transparent",
}) => {
	if (hidden) return null;
	return (
		<span
			style={{
				backgroundColor: bg || "transparent",
				borderColor: border || "transparent",
				color: border,
			}}
			className={`ml-1 mr-2 px-1 py-1 rounded border-2 inline-block
						opacity-${opacity} hover:opacity-100 transition-opacity duration-200
		`}
		>
			<span className="flex items-center justify-center">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="currentColor"
					className="w-3 h-3"
				>
					<path
						fillRule="evenodd"
						d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
						clipRule="evenodd"
					/>
				</svg>
			</span>
		</span>
	);
};
